<template>
  <div class="time-tracking-container">
    <!-- Header Section -->
    <div class="header-section">
      <h2 class="page-title">Responses</h2>
      <v-btn
        text
        color="#fff"
        @click="goToDetails"
        v-if="need"
        class="view-details-btn"
      >
        View Need Details
        <v-icon right>mdi-open-in-new</v-icon>
      </v-btn>
    </div>

    <!-- Shifts Table -->
    <v-card class="shifts-table elevation-1 mt-4">
      <v-data-table
        :headers="headersShift"
        :items="shifts"
        v-model="selectedShifts"
        item-key="_id"
        show-select
        class="elevation-0"
        :hide-default-footer="$vuetify.breakpoint.xs"
      >
        <template v-slot:[`item.title`]="{ item }">
          {{ item.title || `Shift ${shifts.indexOf(item) + 1}` }}
        </template>

        <template v-slot:[`item.begins`]="{ item }">
          {{ formatDate(item.start_date) }}
        </template>

        <template v-slot:[`item.capacity`]="{ item }">
          <span class="capacity-count">
            {{ item.capacity - (item.responses ? item.responses.filter(r => r.status != "Rejected").length : 0) }}
          </span>
          <span class="capacity-divider">of</span>
          <span class="capacity-total">{{ item.capacity }}</span>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <div class="shift-actions">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  @click.stop="onDeleteShift(item)"
                  v-bind="attrs"
                  color="red"
                  v-on="on"
                  class="action-btn delete-btn"
                  v-if="need && need.agency == profile._id"
                >
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </template>
              <span>Delete Shift</span>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Responses Table -->
    <v-card class="responses-table elevation-1 mt-4">
      <v-data-table
        :headers="headers"
        :items="selectedResponses"
        :items-per-page="$vuetify.breakpoint.xs ? -1 : 15"
        :loading="loading"
        class="elevation-0"
        @dblclick:row="goToWorkDiary"
        :hide-default-footer="$vuetify.breakpoint.xs"
      >
        <!-- Avatar Column -->
        <template v-slot:[`item.image`]="{ item }">
          <div
            @click="goToProfile(item.volunteer ? item : item)"
            class="avatar-cell"
          >
            <editable-avatar
              :type="item.volunteer ? 'volunteer' : 'company'"
              :image="
                item.volunteer
                  ? item.volunteer.thumbnail || item.volunteer.image
                  : item.company
                  ? item.company.thumbnail || item.company.image
                  : ''
              "
              :editable="false"
              :size="40"
            />
          </div>
        </template>

        <!-- Name Column -->
        <template v-slot:[`item.name`]="{ item }">
          <div
            class="user-name"
            @click="goToProfile(item.volunteer ? item : item)"
            v-if="item.volunteer || item.company"
          >
            {{
              item.volunteer
                ? `${item.volunteer.first_name} ${item.volunteer.last_name}`
                : item.company.name
            }}
          </div>
          <div v-else class="deleted-user">
            Deleted User
          </div>
        </template>

        <!-- Note Column -->
        <template v-slot:[`item.note`]="{ item }">
          <div class="response-note">
            {{ item.note }}
          </div>
        </template>

        <!-- Date Column -->
        <template v-slot:[`item.createdAt`]="{ item }">
          <div class="response-date">{{ getTimeAgo(item.createdAt) }} ago</div>
        </template>

        <!-- Status Column -->
        <template v-slot:[`item.status`]="{ item }">
          <div class="response-status">
            <v-tooltip bottom v-if="item.status == 'Rejected' && item.reply">
              <template v-slot:activator="{ on, attrs }">
                <v-chip small color="error" dark v-bind="attrs" v-on="on">
                  Rejected
                </v-chip>
              </template>
              <span>Reason: {{ item.reply }}</span>
            </v-tooltip>
            <v-chip v-else small :color="getStatusColor(item.status)" dark>
              {{ item.status }}
            </v-chip>
          </div>
        </template>

        <!-- Actions Column -->
        <template v-slot:[`item.actions`]="{ item }">
          <div class="response-actions">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  @click.stop="detailedView(item)"
                  v-bind="attrs"
                  v-on="on"
                  color="primary"
                  class="action-btn confirm-btn"
                >
                  <v-icon>mdi-eye</v-icon>
                </v-btn>
              </template>
              <span>View</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  @click.stop="onConfirm(item)"
                  v-bind="attrs"
                  v-on="on"
                  color="green"
                  :disabled="item.status == 'Confirmed'"
                  :loading="confirmingResponse == item._id"
                  class="action-btn confirm-btn"
                >
                  <v-icon>mdi-check</v-icon>
                </v-btn>
              </template>
              <span>Confirm</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  @click.stop="onReject(item)"
                  v-bind="attrs"
                  color="red"
                  v-on="on"
                  :disabled="item.status == 'Confirmed' && item.timeslots.some(obj => obj.shift_id === item.shift._id && obj.status === 'Tracking')"
                  class="action-btn reject-btn"
                >
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </template>
              <span>Reject</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  @click.stop="goToDiary(item)"
                  v-bind="attrs"
                  v-on="on"
                  color="blue"
                  class="action-btn diary-btn"
                >
                  <v-icon>mdi-calendar-text</v-icon>
                </v-btn>
              </template>
              <span>Work Hours</span>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Dialogs -->
    <confirm-delete-shift-dialog
      :deleteDialog="deleteShiftDialog"
      :shift="selectedShift"
      :onClose="onCloseConfirmDelete"
      :onDeleted="onShiftDeleted"
    />

    <reject-response-dialog
      :dialog="rejectDialog"
      :need="need"
      :shiftTitle="selectedShiftTitle"
      :response="selectedResponse"
      :onCloseDialog="onCloseRejectDialog"
      :onRejected="onRejectedResponse"
    />

    <agency-response-detailed-dialog
      :item="selected"
      :dialog="detailDialog"
      :onCloseDialog="onCloseDetailedDialog"
    />

    <v-snackbar v-model="snackbar" :timeout="3000" color="primary" bottom>
      {{ snackMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import dateFormat from "dateformat";
import EditableAvatar from "@/components/EditableAvatar.vue";
import ConfirmDeleteShiftDialog from "@/components/common/ConfirmDeleteShiftDialog.vue";
import RejectResponseDialog from "./RejectResponseDialog.vue";
import AgencyResponseDetailedDialog from "./AgencyResponseDetailedDialog.vue";
export default {
  components: {
    EditableAvatar,
    ConfirmDeleteShiftDialog,
    RejectResponseDialog,
    AgencyResponseDetailedDialog
  },
  data() {
    return {
      loading: false,
      snackbar: false,
      snackMessage: null,
      headersShift: [
        { text: "Title", value: "title" },
        { text: "Date", value: "begins" },
        { text: "Duration", value: "hours" },
        { text: "Open Spots", value: "capacity" },
        { text: "Actions", value: "actions", sortable: false }
      ],
      headers: [
        { text: "", value: "image", width: "60px" },
        { text: "Name", value: "name" },
        { text: "Response Date", value: "createdAt", width: "150px" },
        { text: "Note", value: "note" },
        { text: "Status", value: "status", width: "120px" },
        { text: "Actions", value: "actions", width: "180px", sortable: false }
      ],
      shifts: [],
      need: null,
      selectedShifts: [],
      selectedShift: null,
      selectedResponses: [],
      selectedResponse: null,
      deleteShiftDialog: false,
      confirmingResponse: "",
      rejectDialog: false,
      selectedShiftTitle: "",
      detailDialog: false,
      selected: null
    };
  },
  methods: {
    ...mapActions({
      fetchAllResponses: "response/fetchAllResponses",
      confirmResponse: "response/confirmResponse",
      rejectResponse: "response/rejectResponse",
      deleteResponse: "response/deleteResponse",
      fetchAllShifts: "need/fetchAllShifts",
      getNeedDetails: "need/getNeedDetails"
    }),
    onConfirm(response) {
      if (response.volunteer || response.company) {
        this.confirmingResponse = response._id;
        this.confirmResponse(response._id)
          .then(res => {
            this.confirmingResponse = "";
            this.updateResponseStatus(res);
          })
          .catch(error => {
            this.confirmingResponse = "";
            // Show specific message if provided
            if (
              error &&
              error.message === "There is no empty spot on this shift"
            ) {
              this.snackMessage = error.message;
            } else if (
              error &&
              error.response &&
              error.response.data &&
              error.response.data.message
            ) {
              this.snackMessage = error.response.data.message;
            } else if (typeof error === "string") {
              this.snackMessage = error;
            } else {
              this.snackMessage = "An error occurred";
            }
            this.snackbar = true;
            console.log(error);
          });
      } else {
        this.handleDeletedUser(response);
      }
    },
    onReject(response) {
      if (response.volunteer || response.company) {
        this.selectedResponse = response;
        this.rejectDialog = true;
      } else {
        this.handleDeletedUser(response);
      }
    },
    onRejectedResponse(res) {
      this.rejectDialog = false;
      this.updateResponseStatus(res, true);
    },
    onCloseRejectDialog() {
      this.rejectDialog = false;
    },
    onCloseDetailedDialog() {
      this.detailDialog = false;
    },
    updateResponseStatus(res, includeReply = false) {
      // Update in responses list
      const responseIndex = this.responses.findIndex(r => r._id == res._id);
      if (responseIndex >= 0) {
        this.responses[responseIndex].status = res.status;
        if (includeReply) {
          this.responses[responseIndex].reply = res.reply;
        }
      }

      // Update in shifts
      this.shifts = this.shifts.map(shift => {
        const responseInShift = shift.responses.find(r => r._id == res._id);
        if (responseInShift) {
          return {
            ...shift,
            responses: shift.responses.map(r =>
              r._id === res._id
                ? {
                    ...r,
                    status: res.status,
                    ...(includeReply && { reply: res.reply })
                  }
                : r
            )
          };
        }
        return shift;
      });
    },
    handleDeletedUser(response) {
      this.loading = true;
      this.deleteResponse({ _id: response._id })
        .then(() => {
          this.loading = false;
          this.snackMessage = "This user is deleted";
          this.snackbar = true;
          this.getAllShifts();
        })
        .catch(error => {
          this.loading = false;
          console.log(error);
        });
    },
    getTimeAgo(oldTimeStr) {
      const oldTime = Date.parse(oldTimeStr);
      const time = new Date();
      const different = (time.getTime() - oldTime) / 1000;
      const month = (different / 3600 / 24 / 30) | 0;
      const days = (different / 3600 / 24) % 30 | 0;
      const hours = (different / 3600) % 24 | 0;
      const mins = (different / 60) % 60 | 0;

      if (month == 1) return `1 month`;
      if (month) return `${month} months`;
      if (days == 1) return `1 day`;
      if (days) return `${days} days`;
      if (hours == 1) return `1 hour`;
      if (hours) return `${hours} hours`;
      if (mins == 1) return `1 min`;
      if (mins) return `${mins} min`;
      return `less than 1 min`;
    },
    getAllShifts() {
      this.fetchAllShifts({ need_id: this.$route.query._id })
        .then(res => {
          this.shifts = res;
          this.selectedShifts = res;
          if (this.$route.query.selected_shift) {
            this.selectedShifts = this.selectedShifts.filter(
              s => s._id == this.$route.query.selected_shift
            );
          }
        })
        .catch(error => {
          console.log(error.response.data.message);
        });
    },
    goToProfile(item) {
      if (item.volunteer && item.volunteer.type === "Volunteer") {
        let id = item.volunteer._id;
        let routeData = this.$router.resolve({
          name: "volunteer-profile-events",
          params: { id }
        });
        window.open(routeData.href, "_blank");
      } else if (item.company && item.company.type === "Company") {
        let id = item.company._id;
        let routeData = this.$router.resolve({
          name: "company-profile-details",
          params: { id }
        });
        window.open(routeData.href, "_blank");
      }
    },
    detailedView(i) {
      this.detailDialog = true;
      this.selected = i;
    },
    formatDate(date) {
      return dateFormat(this.convertTZ(date, this.timezone), "mm/dd/yyyy");
    },
    convertTZ(date, tzString) {
      if (!date) return "";
      return new Date(
        (typeof date === "string" ? new Date(date) : date).toLocaleString(
          "en-US",
          {
            timeZone: tzString ? tzString : "America/New_York"
          }
        )
      );
    },
    getNeed() {
      this.loading = true;
      this.getNeedDetails({ _id: this.$route.query._id })
        .then(need => {
          this.loading = false;
          this.need = need;
          if (
            this.profile &&
            this.profile._id &&
            need &&
            need.agency != this.profile._id
          ) {
            this.$router.push({
              name: "404"
            });
          }
        })
        .catch(error => {
          this.loading = false;
          if (error.response?.status === 401) {
            this.tokenDialog = true;
          } else {
            console.log(error.response?.data?.message);
          }
        });
    },
    onDeleteShift(shift) {
      this.selectedShift = shift;
      this.deleteShiftDialog = true;
    },
    onCloseConfirmDelete() {
      this.deleteShiftDialog = false;
    },
    onShiftDeleted(shift) {
      this.shifts = this.shifts.filter(s => s._id != shift._id);
      this.deleteShiftDialog = false;
    },
    goToDetails() {
      this.$router.push({
        name: "agency-need-details",
        query: {
          _id: this.need._id
        }
      });
    },
    goToDiary(item) {
      if (this.need && item.volunteer) {
        this.$router.push({
          name: "agency-responses-diary",
          query: {
            need_id: this.need._id,
            volunteer_id: item.volunteer._id
          }
        });
      } else if (this.need && item.company) {
        this.$router.push({
          name: "agency-responses-diary",
          query: {
            need_id: this.need._id,
            company_id: item.company._id
          }
        });
      }
    },
    goToWorkDiary(event, { item }) {
      if (item.volunteer) {
        this.goToDiary(item);
      }
    },
    getStatusColor(status) {
      switch (status) {
        case "Rejected":
          return "error";
        case "Confirmed":
          return "success";
        case "Pending":
          return "warning";
        default:
          return "grey";
      }
    }
  },
  computed: {
    ...mapGetters({
      responses: "response/getAllResponses",
      timezone: "usersetting/getTimeZone"
    }),
    ...mapState("auth", {
      profile: "profile",
      profileType: "type"
    })
  },
  watch: {
    profile(newValue) {
      if (newValue && this.need && this.need.agency != newValue._id) {
        this.$router.push({
          name: "404"
        });
      }
    },
    // selectedShifts(newValue) {
    //   this.selectedResponses = this.responses.filter(response =>
    //     newValue.some(shift => shift._id === response.shift._id)
    //   );
    // },
    selectedShifts(newValue) {
      this.selectedResponses = this.responses.filter(response =>
        newValue.some(
          shift =>
            shift &&
            shift._id &&
            response.shift &&
            response.shift._id &&
            shift._id === response.shift._id
        )
      );
    },
    selectedResponse(newValue) {
      if (newValue) {
        const shiftIndex = this.shifts.findIndex(
          s => s._id === newValue.shift._id
        );
        if (shiftIndex >= 0) {
          this.selectedShiftTitle =
            newValue.shift.title || `Shift ${shiftIndex + 1}`;
        }
      }
    }
  },
  mounted() {
    this.getNeed();
    this.loading = true;
    this.fetchAllResponses({ need_id: this.$route.query._id })
      .then(() => {
        this.loading = false;
        this.getAllShifts();
      })
      .catch(error => {
        this.loading = false;
        if (error.response?.status === 401) {
          this.tokenDialog = true;
        } else {
          console.log(error.response?.data?.message);
        }
      });
  }
};
</script>

<style scoped>
.time-tracking-container {
  min-width: 70%;
  margin: 0 auto;
  padding: 24px;
}

/* Header Section */
.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.page-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #fff;
  margin: 0;
}

.view-details-btn {
  text-transform: none;
  letter-spacing: normal;
  font-weight: 500;
}

/* Tables */
.shifts-table,
.responses-table {
  border-radius: 12px;
  overflow: hidden;
}

/* Shift Table Specific */
.capacity-count {
  font-weight: 600;
  color: #1e293b;
}

.capacity-divider {
  margin: 0 4px;
  color: #64748b;
}

.capacity-total {
  color: #64748b;
}

.shift-actions {
  display: flex;
  justify-content: flex-end;
}

/* Response Table Specific */
.avatar-cell {
  cursor: pointer;
}

.user-name {
  color: #0a5b8a;
  cursor: pointer;
  font-weight: 500;
  transition: color 0.2s;
}

.user-name:hover {
  color: #083c5e;
  text-decoration: underline;
}

.deleted-user {
  color: #94a3b8;
  font-style: italic;
}

.response-note {
  max-width: 300px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.response-date {
  color: #64748b;
  font-size: 0.875rem;
}

.response-actions {
  display: flex;
  justify-content: flex-end;
}

.action-btn {
  margin: 0 4px;
  width: 32px;
  height: 32px;
}

.confirm-btn {
  color: #4caf50;
}

.reject-btn {
  color: #f44336;
}

.diary-btn {
  color: #2196f3;
}

.delete-btn {
  color: #f44336;
}

/* Responsive Adjustments */
@media (max-width: 960px) {
  .time-tracking-container {
    padding: 16px;
  }

  .page-title {
    font-size: 1.25rem;
  }

  .response-note {
    max-width: 200px;
  }
}

@media (max-width: 600px) {
  .header-section {
    flex-direction: column;
    align-items: flex-start;
  }

  .view-details-btn {
    margin-top: 8px;
    align-self: flex-end;
  }

  .response-note {
    max-width: 150px;
  }
}
</style>
